/**
 * Times on the page, always in Mountain time whatever the device's zone, and
 * freshness from the real clock: quiet information, never a warning.
 */

import { MOUNTAIN_ZONE, mountainFormatter } from '../../shared/mountain-clock'

export const TZ = MOUNTAIN_ZONE

const timeFormat = mountainFormatter('en-US', { hour: 'numeric', minute: '2-digit' })
const dayFormat = mountainFormatter('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })
const shortDay = mountainFormatter('en-US', { month: 'short', day: 'numeric' })
const shortDayYear = mountainFormatter('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** The Mountain-time calendar day, "2026-01-17". */
export const dayKey = (d: Date) => dayFormat.format(d)

/** "3:18 PM", "3:00 PM". Some engines put a narrow space before AM/PM. */
export const clockTime = (d: Date) => timeFormat.format(d).replace(/[\u202f\u00a0]/g, ' ')

/** "Jan 17", or "Nov 22, 2025" when the year isn't now's. */
function shortDate(d: Date, now: Date): string {
  const sameYear = dayKey(d).slice(0, 4) === dayKey(now).slice(0, 4)
  return (sameYear ? shortDay : shortDayYear).format(d)
}

/** "09:00" (the report's local Mountain time) → "9 AM"; "15:30" → "3:30 PM". */
export function hourText(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number)
  const ap = h >= 12 ? 'PM' : 'AM'
  const hh = ((h + 11) % 12) + 1
  return m ? `${hh}:${String(m).padStart(2, '0')} ${ap}` : `${hh} ${ap}`
}

export interface Freshness {
  /** "Live · checked 4 min ago", "checked Jan 17, 9:05 AM · offline". */
  lead: string
  /** "changed 6:10 AM", when anything last changed more than a minute before the check. */
  changed: string | null
}

/**
 * "Live · checked 4 min ago" only under 15 minutes, and only once this
 * session has heard from the edge (`live`); then minutes up to the hour;
 * then a time today; then a date. `quiet` is one extra word ("offline") that
 * takes away "Live" and follows the check, so a narrow line loses the
 * "changed" part before it loses the word that explains the state.
 */
export function freshnessParts(
  checkedAt: string,
  changedAt: string,
  now: Date,
  quiet?: string,
  live = true,
): Freshness {
  const checked = new Date(checkedAt)
  const changed = new Date(changedAt)
  const min = Math.max(0, Math.round((now.getTime() - checked.getTime()) / 60_000))
  let lead: string
  if (min < 15) lead = `checked ${min < 1 ? 'just now' : `${min} min ago`}`
  else if (min < 60) lead = `checked ${min} min ago`
  else if (dayKey(checked) === dayKey(now)) lead = `checked ${clockTime(checked)}`
  else lead = `checked ${shortDate(checked, now)}, ${clockTime(checked)}`
  if (min < 15 && live && !quiet) lead = `Live · ${lead}`
  if (quiet) lead += ` · ${quiet}`
  let at: string | null = null
  if (checked.getTime() - changed.getTime() > 60_000)
    at =
      dayKey(changed) === dayKey(checked)
        ? clockTime(changed)
        : `${shortDate(changed, now)}, ${clockTime(changed)}`
  return { lead, changed: at && `changed ${at}` }
}

/** The freshness line as one string. */
export function freshness(
  checkedAt: string,
  changedAt: string,
  now: Date,
  quiet?: string,
  live = true,
): string {
  const { lead, changed } = freshnessParts(checkedAt, changedAt, now, quiet, live)
  return changed ? `${lead} · ${changed}` : lead
}

export interface Opening {
  /** "Opens Friday, November 6". */
  title: string
  /** "in 32 days", "tomorrow", "today". */
  when: string
}

/** Opening day, counted in Mountain days from now; null when unknown or already past. */
export function opening(date: string | undefined, now: Date): Opening | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const day = new Date(`${date}T12:00:00Z`)
  if (!Number.isFinite(day.getTime())) return null
  const days = Math.round(
    (day.getTime() - new Date(`${dayKey(now)}T12:00:00Z`).getTime()) / 86_400_000,
  )
  if (days < 0) return null
  const title = day.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
  return {
    title: `Opens ${title}`,
    when: days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`,
  }
}
